/**
 * homebrewValidation.js — Step-level validation for the Mechanic Composer.
 *
 * Provides per-section validation so the composer can show green/red
 * completion indicators and block publish until all sections are valid.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import { validateComposedMechanic } from '../engine/schema';
import {
  VALID_CONTAINER_CONTEXTS,
  VALID_CONTAINER_TYPES,
  VALID_CONDITION_TYPES,
  VALID_COST_TYPES,
  VALID_EFFECT_TYPES,
  VALID_REFRESH_TRIGGERS,
  VALID_PASSIVE_STATS,
} from '../engine/schema';

/**
 * Validate the Basics section (meta fields).
 */
export function validateBasics(config) {
  const errors = [];
  const meta = config?.meta;
  if (!meta) {
    return { valid: false, errors: ['Meta is required'] };
  }
  if (!meta.name || meta.name.trim() === '') {
    errors.push('Name is required');
  }
  if (!VALID_CONTAINER_CONTEXTS.includes(meta.containerContext)) {
    errors.push('Container context must be "ability" or "domain-card"');
  }
  if (meta.containerType && !VALID_CONTAINER_TYPES.includes(meta.containerType)) {
    errors.push(`Container type must be one of: ${VALID_CONTAINER_TYPES.join(', ')}`);
  }
  if (meta.source !== 'homebrew') {
    errors.push('Source must be "homebrew"');
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate the Actions section.
 */
export function validateActions(config) {
  const errors = [];
  const actions = config?.actions || [];
  const isDisplayOnly = config?.meta?.displayOnly === true;

  if (!isDisplayOnly && actions.length === 0) {
    const hasPassives = (config?.passives || []).length > 0;
    if (!hasPassives) {
      errors.push('At least one action is required (unless display-only or passives-only)');
    }
  }

  actions.forEach((action, i) => {
    if (!action.label || action.label.trim() === '') {
      errors.push(`Action ${i + 1}: label is required`);
    }
    if (!action.effects || action.effects.length === 0) {
      errors.push(`Action ${i + 1}: at least one effect is required`);
    }
    (action.conditions || []).forEach((cond, j) => {
      if (!VALID_CONDITION_TYPES.includes(cond.type)) {
        errors.push(`Action ${i + 1}, condition ${j + 1}: invalid type "${cond.type}"`);
      }
    });
    (action.costs || []).forEach((cost, j) => {
      if (!VALID_COST_TYPES.includes(cost.type)) {
        errors.push(`Action ${i + 1}, cost ${j + 1}: invalid type "${cost.type}"`);
      }
    });
    (action.effects || []).forEach((effect, j) => {
      if (!VALID_EFFECT_TYPES.includes(effect.type)) {
        errors.push(`Action ${i + 1}, effect ${j + 1}: invalid type "${effect.type}"`);
      }
    });
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Validate the Tokens section.
 */
export function validateTokens(config) {
  const errors = [];
  const tokens = config?.tokens || [];

  // Tokens are optional — empty is valid
  tokens.forEach((token, i) => {
    if (!token.id || token.id.trim() === '') {
      errors.push(`Token ${i + 1}: id is required`);
    }
    if (!token.name || token.name.trim() === '') {
      errors.push(`Token ${i + 1}: name is required`);
    }
    const isExpr = token.max && typeof token.max === 'object' && token.max.$ref;
    if (!isExpr && (typeof token.max !== 'number' || token.max < 1)) {
      errors.push(`Token ${i + 1}: max must be at least 1 (or a character trait)`);
    }
    if (!VALID_REFRESH_TRIGGERS.includes(token.refreshOn)) {
      errors.push(`Token ${i + 1}: refreshOn must be one of: ${VALID_REFRESH_TRIGGERS.join(', ')}`);
    }
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Validate the Passives section.
 */
export function validatePassives(config) {
  const errors = [];
  const passives = config?.passives || [];

  // Passives are optional — empty is valid
  passives.forEach((passive, i) => {
    if (!VALID_PASSIVE_STATS.includes(passive.stat)) {
      errors.push(`Passive ${i + 1}: stat must be one of: ${VALID_PASSIVE_STATS.join(', ')}`);
    }
    if (typeof passive.amount !== 'number') {
      errors.push(`Passive ${i + 1}: amount must be a number`);
    }
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Validate the Display section.
 */
export function validateDisplay(config) {
  const errors = [];
  const display = config?.display;

  // Display is optional — empty/undefined is valid
  if (!display) return { valid: true, errors: [] };

  const validLayouts = ['single-button', 'dual-button', 'triple-button', 'button-group', 'counter-only'];
  if (display.layout && !validLayouts.includes(display.layout)) {
    errors.push(`Display layout must be one of: ${validLayouts.join(', ')}`);
  }

  if (display.counterDisplay) {
    if (!display.counterDisplay.tokenId) {
      errors.push('Counter display requires a tokenId');
    } else {
      const tokenIds = (config?.tokens || []).map(t => t.id);
      if (!tokenIds.includes(display.counterDisplay.tokenId)) {
        errors.push(`Counter display tokenId "${display.counterDisplay.tokenId}" does not match any defined token`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Route validation to the correct step validator.
 */
export function validateStep(stepKey, config) {
  switch (stepKey) {
    case 'basics': return validateBasics(config);
    case 'actions': return validateActions(config);
    case 'tokens': return validateTokens(config);
    case 'passives': return validatePassives(config);
    case 'display': return validateDisplay(config);
    default: return { valid: true, errors: [] };
  }
}

/**
 * Get overview status for all sections. Used by the overview bar pills.
 *
 * States: 'complete' | 'error' | 'incomplete' | 'optional'
 * - 'optional': empty optional section (Tokens, Passives, Display) — gray with +
 * - 'incomplete': required section not yet started
 * - 'error': section has content but validation fails
 * - 'complete': section has valid content
 */
export function getOverviewStatus(config) {
  const basics = validateBasics(config);
  const actions = validateActions(config);
  const tokens = validateTokens(config);
  const passives = validatePassives(config);
  const display = validateDisplay(config);

  const tokensEmpty = !config?.tokens || config.tokens.length === 0;
  const passivesEmpty = !config?.passives || config.passives.length === 0;
  const displayEmpty = !config?.display || Object.keys(config.display).length === 0;

  return {
    basics: basics.valid ? 'complete' : 'error',
    actions: actions.valid ? 'complete' : (config?.actions?.length > 0 ? 'error' : 'incomplete'),
    tokens: tokensEmpty ? 'optional' : (tokens.valid ? 'complete' : 'error'),
    passives: passivesEmpty ? 'optional' : (passives.valid ? 'complete' : 'error'),
    display: displayEmpty ? 'optional' : (display.valid ? 'complete' : 'error'),
  };
}

/**
 * Full validation — combines engine schema validation with step validators.
 * Used before publishing.
 */
export function validateFull(config) {
  const allErrors = [];

  // Step-level validation
  const steps = ['basics', 'actions', 'tokens', 'passives', 'display'];
  steps.forEach(step => {
    const result = validateStep(step, config);
    allErrors.push(...result.errors);
  });

  // Engine schema validation (requires id + version for publish)
  if (config.id && config.version) {
    const schemaResult = validateComposedMechanic(config);
    // Only add errors that aren't already covered by step validators
    schemaResult.errors.forEach(err => {
      if (!allErrors.some(e => e.includes(err.split(':')[0]))) {
        allErrors.push(err);
      }
    });
  }

  return { valid: allErrors.length === 0, errors: allErrors };
}
